import * as actionTypes from 'constants/action-types';
import { ActionCreator, Dispatch } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { ApplicationState } from 'store/rootReducer';
import { MockServiceActionTypes } from 'store/mockServices/mockServiceActionTypes';
import { pollTrackingServiceForLogs } from 'axios/axios-polling';
import JSZip from 'jszip';
import FileSaver from 'file-saver';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import i18n from 'i18n';
import { MockService } from 'types/rest/mockServiceTypes';
import { isBase64 } from '../../../utils/miscUtil';
import acApi from '../../../axios/ac/acApi';

export const getRunnerLogsWithPolling: any = async (serviceMockId: string, numberOfLines: number) => {
  //Initiate the process for getting the logs
  const response = await acApi.mockServices.refreshLogs(serviceMockId, numberOfLines);
  if (!response || !response.data || !response.data.result || !response.data.result.trackingId) {
    throw new Error(i18n.t('mockServices:errorMsgs.errorRetrevingTrackingId'));
  }
  const trackingId = response.data.result.trackingId;

  //Wait until the logs are available
  await pollTrackingServiceForLogs(trackingId);

  //Get the logs
  return await acApi.mockServices.getLogs(serviceMockId, numberOfLines);
};

export const setOpenWindowId = (uniqueWindowId: number) => {
  return {
    type: actionTypes.SET_OPEN_WINDOW_ID,
    payload: uniqueWindowId,
  };
};

export const getLogsFromDb: any = (serviceMockId: string, numberOfLines: number) => {
  return acApi.mockServices.getLogs(serviceMockId, numberOfLines);
};

const mapLogsWithTxnId = (logs: any[]): any[] => {
  if (!logs) {
    return [];
  }
  return logs.map((log, index) => {
    log.txnId = index;
    return log;
  });
};

export const refreshMockServiceLogsThunk: ActionCreator<ThunkAction<
  any,
  ApplicationState,
  null,
  MockServiceActionTypes
>> = (
  serviceMockId: string,
  numberOfLines: number,
  uniqueWindowId: number,
  refresh: boolean,
  serviceMockName: string,
): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch({ type: actionTypes.TOGGLE_LOADING_LOGS, payload: uniqueWindowId });
      let response;
      if (refresh) {
        response = await getRunnerLogsWithPolling(serviceMockId, numberOfLines);
      } else {
        response = await getLogsFromDb(serviceMockId, numberOfLines);
      }

      if (response.data?.result) {
        response.data.result.httpRunnerLog = mapLogsWithTxnId(response.data.result.httpRunnerLog || []);
        response.data.result.messagingRunnerLog = mapLogsWithTxnId(response.data.result.messagingRunnerLog || []);
      }

      dispatch({
        type: actionTypes.GET_LOGS,
        payload: {
          ...response.data,
          serviceMockId: serviceMockId,
          uniqueWindowId: uniqueWindowId,
        },
      });
    } catch (err) {
      dispatch({ type: actionTypes.TOGGLE_LOADING_LOGS, payload: uniqueWindowId });
      dispatch(
        showToastNotification(
          ToastType.Danger,
          i18n.t('errorMsgs.networkProblem'),
          i18n.t('mockServices:errorMsgs.failedToRetrieveLogs', {
            name: serviceMockName,
          }),
        ),
      );
      console.log('Error', err);
    }
  };
};

export const downloadMockServiceLogsThunk: ActionCreator<ThunkAction<
  any,
  ApplicationState,
  null,
  MockServiceActionTypes
>> = (mockService: MockService): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: Dispatch, getState: any) => {
    try {
      const zip = new JSZip();
      zip.file(
        mockService.name + '_logs.txt',
        JSON.stringify(getState().mockservices.serviceLogs.result, function(key, value) {
          if (key === 'body') {
            if (value && isBase64(value)) {
              return value ? Buffer.from(value, 'base64').toString() : '';
            } else {
              return value;
            }
          } else {
            return value;
          }
        }),
      );
      zip
        .generateAsync({
          type: 'blob',
          compression: 'DEFLATE',
          compressionOptions: {
            level: 5,
          },
        })
        .then(function(content: any) {
          FileSaver.saveAs(content, mockService.name + '_logs.zip');
        });
    } catch (err) {
      dispatch(
        showToastNotification(
          ToastType.Danger,
          i18n.t('errorMsgs.networkProblem'),
          i18n.t('mockServices:errorMsgs.failedToDownloadLogs', {
            name: getState().mockServiceDetails.mockService.name,
          }),
        ),
      );
      console.log('Error', err);
    }
  };
};
